import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useSiteWeather } from '../../hooks/useSiteWeather';
import { getWeatherIconComponent } from './weatherIconMap';

const paperSx = {
  p: 1.5,
  borderRadius: 2,
  borderColor: 'rgba(15,23,42,0.12)',
  background: '#ffffff',
  boxShadow: '0 2px 8px rgba(15, 23, 42, 0.12)',
  color: 'text.primary',
  minWidth: 0,
};

const formatTemp = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return `${Math.round(Number(value))}°C`;
};

const formatWind = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return `${Math.round(Number(value))} km/h`;
};

const formatPct = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return `${Math.round(Number(value))}%`;
};

const weekdayLabel = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString(undefined, { weekday: 'short' });
};

/**
 * Reusable Signal Bridge weather card.
 * Requires an explicit siteId — does not infer a global current site.
 */
export default function WeatherCard({ siteId, siteName }) {
  const id = String(siteId || '').trim();
  const {
    weather,
    isLoading,
    isRefreshing,
    isError,
    error,
    errorKind,
    stale,
    refreshCooledDown,
    refresh,
  } = useSiteWeather(id, { enabled: Boolean(id) });

  const locationLabel = useMemo(() => {
    const loc = weather?.location || {};
    const parts = [loc.name, loc.region].filter(Boolean);
    if (parts.length) return parts.join(', ');
    return siteName || id || 'Weather';
  }, [weather, siteName, id]);

  if (!id) {
    return (
      <Paper variant="outlined" sx={paperSx} data-testid="weather-card-empty-site">
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
          Weather
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Select a site to view weather.
        </Typography>
      </Paper>
    );
  }

  if (isLoading) {
    return (
      <Paper variant="outlined" sx={{ ...paperSx, display: 'flex', alignItems: 'center', gap: 1 }} data-testid="weather-card-loading">
        <CircularProgress size={16} />
        <Typography variant="body2">Loading weather…</Typography>
      </Paper>
    );
  }

  if (errorKind === 'WEATHER_LOCATION_MISSING') {
    return (
      <Paper variant="outlined" sx={paperSx} data-testid="weather-card-not-configured">
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
          Weather
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Weather is not configured for this site.
        </Typography>
      </Paper>
    );
  }

  if (isError || (!weather && error)) {
    return (
      <Paper variant="outlined" sx={paperSx} data-testid="weather-card-error">
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
          Weather
        </Typography>
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          Unable to load weather right now.
        </Typography>
        <Button size="small" variant="outlined" onClick={() => refresh()} startIcon={<RefreshIcon fontSize="small" />}>
          Retry
        </Button>
      </Paper>
    );
  }

  if (!weather) {
    return null;
  }

  const CurrentIcon = getWeatherIconComponent(weather?.current?.conditionIcon);
  const forecast = Array.isArray(weather.forecast) ? weather.forecast.slice(0, 7) : [];

  return (
    <Paper variant="outlined" sx={paperSx} data-testid="weather-card">
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.35 }}>
            Weather
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 750, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            title={locationLabel}
          >
            {locationLabel}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          {stale ? (
            <Chip size="small" label="Updated earlier · data may be stale" sx={{ maxWidth: { xs: 140, sm: 'none' } }} />
          ) : null}
          {refreshCooledDown && !isRefreshing ? (
            <Chip size="small" variant="outlined" label="Weather was refreshed recently" />
          ) : null}
          <Tooltip title="Refresh weather">
            <span>
              <IconButton
                size="small"
                aria-label="Refresh weather"
                onClick={() => refresh()}
                disabled={isRefreshing}
              >
                {isRefreshing ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 220px) minmax(0, 1fr)' },
          alignItems: 'stretch',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', minWidth: 0 }}>
          <CurrentIcon sx={{ fontSize: 48, color: 'warning.main', flexShrink: 0 }} aria-hidden />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h4"
              sx={{ fontWeight: 800, lineHeight: 1, fontSize: { xs: '2rem', md: '2.15rem' } }}
              data-testid="weather-current-temp"
            >
              {formatTemp(weather?.current?.temperatureC)}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }} data-testid="weather-current-condition">
              {weather?.current?.condition || '—'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Feels like {formatTemp(weather?.current?.feelsLikeC)}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, minmax(0, 1fr))' },
            gap: 1,
            alignContent: 'center',
          }}
        >
          <Detail label="High / Low" value={`${formatTemp(weather?.today?.highC)} / ${formatTemp(weather?.today?.lowC)}`} />
          <Detail label="Precip" value={formatPct(weather?.today?.precipitationChance)} />
          <Detail
            label="Wind"
            value={`${formatWind(weather?.current?.windKph)}${weather?.current?.windDirection ? ` ${weather.current.windDirection}` : ''}`}
          />
          <Detail
            label="Humidity"
            value={weather?.current?.humidity == null ? '—' : `${weather.current.humidity}%`}
          />
        </Box>
      </Box>

      <Box
        sx={{
          mt: 1.5,
          display: 'flex',
          gap: 1,
          overflowX: 'auto',
          pb: 0.5,
          WebkitOverflowScrolling: 'touch',
        }}
        data-testid="weather-forecast-row"
      >
        {forecast.map((day) => {
          const DayIcon = getWeatherIconComponent(day?.conditionIcon);
          return (
            <Box
              key={day?.date || day?.condition}
              sx={{
                flex: '0 0 auto',
                width: { xs: 76, sm: 84 },
                p: 1,
                borderRadius: 1.5,
                border: '1px solid rgba(15,23,42,0.08)',
                bgcolor: 'rgba(243,244,246,0.65)',
                textAlign: 'center',
              }}
              data-testid="weather-forecast-day"
            >
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                {weekdayLabel(day?.date)}
              </Typography>
              <DayIcon sx={{ fontSize: 22, my: 0.35, color: 'text.secondary' }} aria-hidden />
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>
                {formatTemp(day?.highC)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {formatTemp(day?.lowC)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {formatPct(day?.precipitationChance)}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}

function Detail({ label, value }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block' }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: 650, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        title={String(value)}
      >
        {value}
      </Typography>
    </Box>
  );
}
